import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, CalendarDays, Clock3, MapPin, CheckCircle2, ShieldCheck, ListChecks,
  XCircle, Info, Repeat,
} from "lucide-react";
import config, { fetchRemoteConfig, getMembershipPricing } from "../../config";
import {
  getPlanById, getTimeSlotsForPlan, formatInr, formatVisits,
  formatServicesIncluded, addDays,
} from "./membershipPlansData";
import GoogleMapPreview from "../common/GoogleMapPreview";
import CurrentLocationDetector from "../common/CurrentLocationDetector";
import CurrentLocationCard from "../common/CurrentLocationCard";

const BASE_URL = config.API_BASE_URL || "http://localhost:8000";

// ── Auth / address helpers (same pattern as SummaryPage.js) ────────────────
const getLoggedInUser = () => {
  try { return JSON.parse(localStorage.getItem("currentUser") || "null"); }
  catch { return null; }
};
const getAuthToken = () =>
  localStorage.getItem("authToken")    ||
  localStorage.getItem("accessToken")  ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("token") || "";
const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

// Plans can only start 2 days from today — booking today or tomorrow isn't
// allowed, the earliest selectable start is the day after tomorrow.
const minStartDateStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  return d.toISOString().slice(0, 10);
};

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) { resolve(true); return; }
  const script = document.createElement("script");
  script.src = "https://checkout.razorpay.com/v1/checkout.js";
  script.onload  = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

// ── Current Location (Google API when configured, else Testing Mode) ──────
// Same contract as InstantSummaryPage.js / ScheduleSummaryPage.js.
const GMAPS_KEY            = config?.GOOGLE_MAPS_API_KEY || "";
const IS_GOOGLE_CONFIGURED = Boolean(GMAPS_KEY);

const DUMMY_TEST_LOCATIONS = [
  { id: "test-1", label: "Current Location", line1: "MG Road",          area: "MG Road",          city: "Bengaluru", state: "Karnataka", pincode: "560001", lat: 12.9757, lng: 77.6069 },
  { id: "test-2", label: "Current Location", line1: "Devaraja Mohalla", area: "Devaraja Mohalla", city: "Mysuru",     state: "Karnataka", pincode: "570001", lat: 12.3052, lng: 76.6552 },
  { id: "test-3", label: "Current Location", line1: "Whitefield",       area: "Whitefield",        city: "Bengaluru", state: "Karnataka", pincode: "560066", lat: 12.9698, lng: 77.7500 },
];

// ─────────────────────────────────────────────────────────────────────────────
// STEP 2 — PAYMENT
// A real intermediate step (mirrors PaymentPage.js for one-off orders):
// the Razorpay order is created BEFORE this screen renders; this screen is
// where the user explicitly triggers verify + activation. Nothing gets
// activated just by filling in the details on step 1.
// ─────────────────────────────────────────────────────────────────────────────
const PaymentStep = ({ plan, paymentOrder, bookingDetails, onBack, onActivated, onSlotConflict }) => {
  const [paying, setPaying] = useState(false);
  const [error, setError]   = useState("");

  const verifyPayment = async (body) => {
    const verifyRes = await fetch(`${BASE_URL}/api/payment/verify/`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(body),
    });
    const verifyData = await verifyRes.json();
    if (!verifyRes.ok || !verifyData?.success) {
      throw new Error(verifyData?.message || "Payment verification failed.");
    }
    return verifyData;
  };

  // Activate the membership once a payment has been verified.
  const activateMembership = async (verifyData) => {
    const purchaseRes = await fetch(`${BASE_URL}/api/user/membership/purchase/`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({
        ...bookingDetails,
        razorpay_payment_id:     verifyData.payment_id,
        razorpay_order_id:       paymentOrder.order_id,
        razorpay_transaction_id: verifyData.transaction_id,
        payment_status:          "paid",
      }),
    });
    const purchaseData = await purchaseRes.json();
    if (!purchaseRes.ok || !purchaseData?.success) {
      // Slot filled between step 1 and payment — send the user back to
      // pick a different date/slot instead of letting them retry the
      // exact same (now-full) booking over and over.
      if (purchaseRes.status === 409) {
        onSlotConflict(purchaseData?.message || "That visit slot just filled up. Please choose a different date or time.");
        return;
      }
      throw new Error(purchaseData?.message || "Could not activate membership.");
    }
    onActivated(purchaseData);
  };

  const handlePay = async () => {
    if (paying) return;
    setPaying(true);
    setError("");
    try {
      if (paymentOrder.is_test) {
        // Test mode — no real payment gateway involved; the backend's
        // verify endpoint always succeeds for this mode, so there's
        // nothing to sign or check.
        const verifyData = await verifyPayment({
          razorpay_order_id:   paymentOrder.order_id,
          razorpay_payment_id: `pay_TEST_${Date.now()}`,
        });
        await activateMembership(verifyData);
        return;
      }

      // Live mode — open Razorpay's real checkout widget and only proceed
      // once it reports back a genuinely signed payment (same pattern as
      // PaymentPage.js's one-off service checkout).
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("Razorpay failed to load. Please check your connection and try again.");

      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key:         paymentOrder.key_id,
          amount:      paymentOrder.amount,
          currency:    paymentOrder.currency,
          order_id:    paymentOrder.order_id,
          name:        "K2Karmika",
          description: `${plan.name} Membership`,
          handler: async (response) => {
            try {
              const verifyData = await verifyPayment({
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
              });
              await activateMembership(verifyData);
              resolve();
            } catch (e) { reject(e); }
          },
          modal: {
            // User closed the popup without paying — not an error.
            ondismiss: () => resolve(),
          },
          theme: { color: "#4f46e5" },
        });
        rzp.on("payment.failed", (resp) => {
          reject(new Error(resp?.error?.description || "Payment failed."));
        });
        rzp.open();
      });
    } catch (e) {
      setError(e.message || "Payment failed. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={{ ...S.container, maxWidth: "620px" }}>
        <button type="button" style={S.backLink} onClick={onBack}><ArrowLeft size={14} /> Back to Details</button>

        {paymentOrder.is_test && (
          <div style={S.testBanner}>🧪 <strong>Test Mode</strong> — no real payment will be processed.</div>
        )}

        <div style={S.card}>
          <div style={S.cardLabel}>Complete Your Payment</div>
          <p style={{ ...S.includedNote, marginBottom: "18px" }}>
            Razorpay Order ID: <strong>{paymentOrder.order_id}</strong>
          </p>

          <div style={S.summaryRow}><span>Plan</span><span style={S.summaryVal}>{plan.name}</span></div>
          <div style={S.summaryRow}><span>Visit Slot</span><span style={S.summaryVal}>{bookingDetails.preferred_slot}</span></div>
          <div style={S.summaryRow}><span>Start Date</span><span style={S.summaryVal}>{bookingDetails.start_date}</span></div>
          <div style={S.summaryDivider} />
          <div style={{ ...S.summaryRow, ...S.summaryTotal }}><span>Total Payable</span><span>{formatInr(bookingDetails.price)}</span></div>

          {error && <div style={S.errorBox}>{error}</div>}

          <button
            style={{ ...S.primaryBtn, opacity: paying ? 0.7 : 1 }}
            disabled={paying}
            onClick={handlePay}
          >
            {paying ? "Processing…" : `Pay ${formatInr(bookingDetails.price)}`}
          </button>
          <div style={S.secureNote}><ShieldCheck size={13} /> Secured by Razorpay{paymentOrder.is_test ? " (test mode)" : ""}</div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN — step router: "details" -> "payment" -> success
// ─────────────────────────────────────────────────────────────────────────────
const MembershipCheckout = () => {
  const { planId } = useParams();
  const navigate = useNavigate();

  const plan = getPlanById(planId);
  const timeSlots = getTimeSlotsForPlan(planId);

  const loggedInUser = getLoggedInUser();
  const userMobile   = loggedInUser?.mobile || loggedInUser?.phone || "";
  const addressKey   = loggedInUser?.id || loggedInUser?.user_id || userMobile;

  const [step, setStep]             = useState("details"); // "details" | "payment"
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);

  const [startDate, setStartDate]   = useState(minStartDateStr());
  const [slotId, setSlotId]         = useState("");
  const [slotAvailability, setSlotAvailability] = useState([]); // [{slot_id,label,seats,booked,availability}]
  const [loadingSlots, setLoadingSlots]          = useState(false);
  const [addresses, setAddresses]   = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const [selectedAddrId, setSelectedAddrId] = useState("");
  const [locationMode, setLocationMode]       = useState("saved"); // "saved" | "current"
  const [currentLocationAddr, setCurrentLocationAddr] = useState(null);
  const [testLocationId, setTestLocationId]   = useState("");
  const [initiating, setInitiating] = useState(false);
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState(null);

  // Dashboard-editable price/GST overrides (Seat Availability tab ->
  // "Membership Plan Pricing & GST") arrive async — force one re-render once
  // fetched so the plain (unmemoized) getMembershipPricing() reads below
  // pick up the live values instead of whatever was cached at module load.
  const [, forceConfigRerender] = useState(0);
  useEffect(() => {
    fetchRemoteConfig().then(() => forceConfigRerender((n) => n + 1));
  }, []);

  // Redirect to login if not signed in, preserving return path.
  useEffect(() => {
    if (!loggedInUser) {
      navigate("/user/user-login", {
        state: { redirectTo: `/subscription-plans/checkout/${planId}` },
        replace: true,
      });
    }
  }, [loggedInUser, navigate, planId]); // eslint-disable-line

  // Fetch saved addresses once logged in.
  useEffect(() => {
    if (!addressKey) return;
    setAddrLoading(true);
    fetch(`${BASE_URL}/api/user/user-addresses/${encodeURIComponent(addressKey)}/`, {
      credentials: "include", headers: authHeaders(),
    })
      .then((r) => r.json())
      .then((data) => {
        const addrs = Array.isArray(data?.data) ? data.data : [];
        setAddresses(addrs);
        if (addrs.length) setSelectedAddrId(addrs[0].id);
      })
      .catch(() => {})
      .finally(() => setAddrLoading(false));
  }, [addressKey]);

  // Fetch seat availability for the recurring visit slot whenever the plan
  // or start date changes — same atomic "schedule concept" seat model used
  // for regular service bookings, scoped to (plan_id, start_date).
  useEffect(() => {
    if (!planId || !startDate) return;
    let cancelled = false;
    setLoadingSlots(true);
    fetch(`${BASE_URL}/api/membership/slot-availability/`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ plan_id: planId, start_date: startDate }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const slots = Array.isArray(data?.data?.slots) ? data.data.slots : [];
        setSlotAvailability(slots);
        // Deselect if the currently-picked slot is no longer available
        // (filled up by someone else, or start date just changed).
        setSlotId((prev) => {
          const stillOpen = slots.find((s) => s.slot_id === prev)?.availability;
          return stillOpen ? prev : "";
        });
      })
      .catch(() => { if (!cancelled) setSlotAvailability([]); })
      .finally(() => { if (!cancelled) setLoadingSlots(false); });
    return () => { cancelled = true; };
  }, [planId, startDate, step]);

  if (!plan) {
    return (
      <div style={S.page}>
        <div style={{ ...S.container, textAlign: "center", paddingTop: "80px" }}>
          <h2>Plan not found</h2>
          <Link to="/subscription-plans" style={S.backLink}><ArrowLeft size={14} /> Back to Plans</Link>
        </div>
      </div>
    );
  }

  if (!loggedInUser) return null; // redirecting via effect above

  const { price: planPrice, gstPercent } = getMembershipPricing(planId, "flat", plan.price);

  const selectedAddress = addresses.find((a) => a.id === selectedAddrId) || null;
  const effectiveAddress = locationMode === "current" ? currentLocationAddr : selectedAddress;
  const handleSelectTestLocation = (id) => {
    setTestLocationId(id);
    setCurrentLocationAddr(DUMMY_TEST_LOCATIONS.find((l) => l.id === id) || null);
  };
  const canProceed = !!startDate && !!slotId && !!effectiveAddress && !initiating;
  const endDate = startDate ? addDays(startDate, plan.durationDays) : "";

  // Step 1 -> 2: create the Razorpay order, then hand off to PaymentStep.
  // Nothing is activated here — this only reserves/creates the payment order.
  const handleContinueToPayment = async () => {
    if (!canProceed) return;
    setInitiating(true);
    setError("");
    try {
      const slot = timeSlots.find((s) => s.id === slotId);

      const createRes = await fetch(`${BASE_URL}/api/payment/create-order/`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ amount: planPrice, mobile: userMobile }),
      });
      const createData = await createRes.json();
      if (!createRes.ok || !createData?.success) {
        throw new Error(createData?.message || "Could not initiate payment.");
      }

      setBookingDetails({
        mobile:            userMobile,
        plan_id:            plan.id,
        plan_name:          plan.name,
        visits_total:       plan.visitsCount,
        duration_days:      plan.durationDays,
        price:              planPrice,
        preferred_slot:     slot?.label || "",
        slot_id:            slotId,
        start_date:         startDate,
        delivery_address:   effectiveAddress,
        customer_name:      loggedInUser?.name || loggedInUser?.full_name || "",
        customer_email:     loggedInUser?.email || "",
      });
      setPaymentOrder(createData);
      setStep("payment");
    } catch (e) {
      setError(e.message || "Could not initiate payment. Please try again.");
    } finally {
      setInitiating(false);
    }
  };

  if (success) {
    return (
      <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={S.successCard}>
          <div style={{ fontSize: 56 }}>🎉</div>
          <h2 style={S.successTitle}>Membership Activated!</h2>
          <p style={S.successText}>
            {plan.name} is now active — {formatVisits(plan.visitsCount)} available until{" "}
            <strong>{success.expiry_date}</strong>.
          </p>
          <button style={S.primaryBtn} onClick={() => navigate("/profile-dashboard", { state: { openMembership: true } })}>
            Go to My Membership
          </button>
        </div>
      </div>
    );
  }

  if (step === "payment" && paymentOrder && bookingDetails) {
    return (
      <PaymentStep
        plan={plan}
        paymentOrder={paymentOrder}
        bookingDetails={bookingDetails}
        onBack={() => setStep("details")}
        onActivated={(data) => setSuccess(data)}
        onSlotConflict={(msg) => { setError(msg); setSlotId(""); setStep("details"); }}
      />
    );
  }

  return (
    <div style={S.page}>
      <div style={S.container}>
        <Link to="/subscription-plans" style={S.backLink}><ArrowLeft size={14} /> Back to Plans</Link>

        <div style={S.layout}>
          {/* Left: date + slot + address */}
          <div style={S.left}>
            <h1 style={S.title}>Set Up Your {plan.name}</h1>
            <p style={S.subtitle}>Choose your start date and preferred recurring visit slot.</p>

            {plan.packageSummary && (
              <div style={S.overviewBanner}>{plan.packageSummary}</div>
            )}

            <div style={S.card}>
              <div style={S.cardLabel}><CalendarDays size={16} color="#4f46e5" /> Start Date</div>
              <input
                type="date"
                style={S.dateInput}
                value={startDate}
                min={minStartDateStr()}
                onChange={(e) => setStartDate(e.target.value)}
              />
              {endDate && (
                <div style={S.endDateHint}>
                  Plan runs for {plan.durationDays} days — ends on <strong>{endDate}</strong>
                </div>
              )}
            </div>

            <div style={S.card}>
              <div style={S.cardLabel}><Clock3 size={16} color="#4f46e5" /> Preferred Visit Slot (2 hrs)</div>
              <p style={S.slotHint}>
                {loadingSlots ? "Checking seat availability…" : "Seats are shared — once a slot is full, it can't be booked by other members."}
              </p>
              <div style={S.slotGrid}>
                {timeSlots.map((slot) => {
                  const avail    = slotAvailability.find((s) => s.slot_id === slot.id);
                  const full     = avail ? !avail.availability : false;
                  const seatsLeft = avail ? Math.max(0, avail.seats - avail.booked) : null;
                  return (
                    <button
                      key={slot.id}
                      type="button"
                      disabled={full || loadingSlots}
                      style={{
                        ...S.slotBtn,
                        ...(slotId === slot.id ? S.slotBtnActive : {}),
                        ...(full ? S.slotBtnFull : {}),
                      }}
                      onClick={() => !full && setSlotId(slot.id)}
                    >
                      <span>{slot.label}</span>
                      <span style={S.slotSeatsNote}>
                        {full ? "Fully booked" : seatsLeft != null ? `${seatsLeft} seat${seatsLeft === 1 ? "" : "s"} left` : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {Array.isArray(plan.includedServices) && plan.includedServices.length > 0 && (
              <div style={S.card}>
                <div style={S.cardLabel}><ListChecks size={16} color="#4f46e5" /> Included Services</div>
                <p style={S.includedNote}>
                  Every 2-hour visit covers a rotating selection of these tasks —{" "}
                  {formatServicesIncluded(plan)} across the full plan.
                </p>
                <div style={S.serviceChipRow}>
                  {plan.includedServices.map((s) => (
                    <span key={s} style={S.serviceChip}>
                      <CheckCircle2 size={13} color="#4f46e5" /> {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {plan.dailyRoutineNote && (
              <div style={S.routineBanner}>
                <Repeat size={16} color="#4f46e5" />
                <div>
                  <div style={S.routineTitle}>Daily Routine Cleaning</div>
                  <div style={S.routineText}>{plan.dailyRoutineNote}</div>
                </div>
              </div>
            )}

            {Array.isArray(plan.customerNotes) && plan.customerNotes.length > 0 && (
              <div style={S.card}>
                <div style={S.cardLabel}><Info size={16} color="#4f46e5" /> Customer Notes</div>
                <ul style={S.notesList}>
                  {plan.customerNotes.map((note) => (
                    <li key={note.label} style={S.noteItem}>
                      <strong>{note.label}:</strong> {note.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {Array.isArray(plan.excludedServices) && plan.excludedServices.length > 0 && (
              <div style={S.card}>
                <div style={S.cardLabel}><XCircle size={16} color="#b91c1c" /> Excluded Services</div>
                <p style={S.includedNote}>
                  These fall outside this plan's scope and are not covered by a visit.
                </p>
                <div style={S.serviceChipRow}>
                  {plan.excludedServices.map((s) => (
                    <span key={s} style={S.excludedChip}>
                      <XCircle size={13} color="#b91c1c" /> {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div style={S.card}>
              <div style={S.cardLabel}><MapPin size={16} color="#4f46e5" /> Service Address</div>

              <div style={S.addrTabRow}>
                <button type="button" style={{ ...S.addrTab, ...(locationMode === "saved" ? S.addrTabActive : {}) }}
                  onClick={() => setLocationMode("saved")}>
                  📍 Saved Address
                </button>
                <button type="button" style={{ ...S.addrTab, ...(locationMode === "current" ? S.addrTabActive : {}) }}
                  onClick={() => setLocationMode("current")}>
                  🛰 Current Location
                </button>
              </div>

              {locationMode === "saved" ? (
                addrLoading ? (
                  <div style={S.addrState}>Loading your saved addresses…</div>
                ) : addresses.length === 0 ? (
                  <div style={S.addrState}>
                    No saved addresses yet.{" "}
                    <Link to="/profile-dashboard" style={S.inlineLink}>Add one in your profile →</Link>
                  </div>
                ) : (
                  <div style={S.addrList}>
                    {addresses.map((addr) => {
                      const active = selectedAddrId === addr.id;
                      const full = [addr.line1, addr.area, addr.city, addr.state, addr.pincode].filter(Boolean).join(", ");
                      return (
                        <div
                          key={addr.id}
                          style={{ ...S.addrItem, ...(active ? S.addrItemActive : {}) }}
                          onClick={() => setSelectedAddrId(addr.id)}
                        >
                          <input type="radio" readOnly checked={active} style={{ accentColor: "#4f46e5" }} />
                          <div>
                            <div style={S.addrLabel}>{addr.label || "Address"}</div>
                            <div style={S.addrText}>{full}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
                <div>
                  {!IS_GOOGLE_CONFIGURED && (
                    <div style={S.testingBadge}>
                      🧪 Testing Mode — no Google Maps API key configured yet. Pick a dummy location below.
                    </div>
                  )}
                  {IS_GOOGLE_CONFIGURED ? (
                    <CurrentLocationDetector onDetected={setCurrentLocationAddr}
                      buttonStyle={S.detectBtn} errorStyle={S.locationErrorText} />
                  ) : (
                    <select style={S.testSelect} value={testLocationId}
                      onChange={(e) => handleSelectTestLocation(e.target.value)}>
                      <option value="">Select a test location…</option>
                      {DUMMY_TEST_LOCATIONS.map((l) => (
                        <option key={l.id} value={l.id}>{l.line1}, {l.city}</option>
                      ))}
                    </select>
                  )}
                  {currentLocationAddr && (
                    <div style={{ marginTop: "14px" }}>
                      <CurrentLocationCard addr={currentLocationAddr} onChange={setCurrentLocationAddr}
                        cardStyle={S.addrItem}
                        badgeStyle={{ ...S.addrLabel }}
                        blockStyle={S.addrText} />
                      <div style={S.mapCard}>
                        <GoogleMapPreview addr={currentLocationAddr} height={180}
                          emptyTitle="Map preview needs a Google Maps API key" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: plan summary + continue */}
          <div style={S.right}>
            <div style={S.summaryCard}>
              <div style={S.summaryHead}>{plan.highlight && <span style={S.popularTag}>Most Popular</span>}</div>
              <h3 style={S.summaryPlanName}>{plan.name}</h3>
              <p style={S.summaryTagline}>{plan.tagline}</p>

              <div style={S.priceRow}>
                <span style={S.price}>{formatInr(planPrice)}</span>
                <span style={S.period}>{plan.period}</span>
              </div>
              <div style={S.gstNote}>(incl. {gstPercent}% GST)</div>

              <div style={S.summaryDivider} />

              <div style={S.summaryRow}><span>Visits Included</span><span style={S.summaryVal}>{formatVisits(plan.visitsCount)}</span></div>
              <div style={S.summaryRow}><span>Services Included</span><span style={S.summaryVal}>{formatServicesIncluded(plan)}</span></div>
              <div style={S.summaryRow}><span>Valid For</span><span style={S.summaryVal}>{plan.durationDays} days</span></div>
              <div style={S.summaryRow}>
                <span>Visit Slot</span>
                <span style={S.summaryVal}>{timeSlots.find((s) => s.id === slotId)?.label || "—"}</span>
              </div>
              <div style={S.summaryRow}><span>Start Date</span><span style={S.summaryVal}>{startDate || "—"}</span></div>
              <div style={S.summaryRow}><span>End Date</span><span style={S.summaryVal}>{endDate || "—"}</span></div>

              {Array.isArray(plan.includedServices) && plan.includedServices.length > 0 && (
                <div style={S.summaryServicesWrap}>
                  {plan.includedServices.map((s) => (
                    <span key={s} style={S.summaryServiceChip}>{s}</span>
                  ))}
                </div>
              )}

              <div style={S.summaryDivider} />
              <div style={{ ...S.summaryRow, ...S.summaryTotal }}><span>Total Payable</span><span>{formatInr(planPrice)}</span></div>

              {error && <div style={S.errorBox}>{error}</div>}

              <button
                style={{ ...S.primaryBtn, opacity: canProceed ? 1 : 0.55, cursor: canProceed ? "pointer" : "not-allowed" }}
                disabled={!canProceed}
                onClick={handleContinueToPayment}
              >
                {initiating ? "Preparing Payment…" : `Continue to Payment — ${formatInr(planPrice)}`}
              </button>
              <div style={S.secureNote}><ShieldCheck size={13} /> Secured by Razorpay (test mode)</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const S = {
  page:      { background: "#f8fafc", minHeight: "100vh", padding: "28px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "1100px", margin: "0 auto" },
  backLink:  { display: "inline-flex", alignItems: "center", gap: "6px", marginBottom: "18px", color: "#4f46e5", fontWeight: 700, fontSize: "14px", textDecoration: "none", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" },

  layout: { display: "grid", gridTemplateColumns: "1fr 360px", gap: "24px", alignItems: "start" },
  left:   { display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 },
  right:  { minWidth: 0 },

  title:    { margin: 0, fontSize: "28px", fontWeight: 800 },
  subtitle: { margin: "8px 0 4px", color: "#6b7280", fontSize: "14px" },

  overviewBanner: { display: "inline-block", background: "#eef2ff", color: "#4338ca", fontSize: "13px", fontWeight: 800, padding: "9px 16px", borderRadius: "999px", margin: "4px 0 4px" },

  card:      { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "18px 20px" },
  cardLabel: { display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: 800, color: "#111827", marginBottom: "14px" },

  routineBanner: { display: "flex", alignItems: "flex-start", gap: "10px", background: "#f5f6ff", border: "1px solid #e0e3ff", borderRadius: "16px", padding: "14px 16px" },
  routineTitle:  { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "2px" },
  routineText:   { fontSize: "12.5px", color: "#6b7280", lineHeight: 1.6 },

  notesList: { margin: 0, padding: "0 0 0 18px", display: "flex", flexDirection: "column", gap: "10px" },
  noteItem:  { fontSize: "12.5px", color: "#4b5563", lineHeight: 1.6 },

  excludedChip: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#fef2f2", color: "#991b1b", fontSize: "12px", fontWeight: 700, padding: "7px 12px", borderRadius: "999px", border: "1px solid #fecaca" },

  dateInput: { width: "100%", maxWidth: "260px", height: "44px", padding: "0 14px", borderRadius: "12px", border: "1px solid #d6deeb", fontSize: "14px", outline: "none", boxSizing: "border-box" },
  endDateHint: { marginTop: "10px", fontSize: "12px", color: "#6b7280" },

  slotHint: { margin: "-6px 0 12px", fontSize: "12px", color: "#6b7280" },
  slotGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px" },
  slotBtn:  { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px", border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", borderRadius: "12px", padding: "12px 10px", fontSize: "13px", fontWeight: 700, cursor: "pointer" },
  slotBtnActive: { border: "1.5px solid #4f46e5", background: "#eef2ff", color: "#4338ca" },
  slotBtnFull: { background: "#f9fafb", color: "#9ca3af", borderColor: "#e5e7eb", cursor: "not-allowed", opacity: 0.75 },
  slotSeatsNote: { fontSize: "11px", fontWeight: 600, color: "#9ca3af" },

  includedNote:   { margin: "0 0 14px", fontSize: "12px", color: "#6b7280", lineHeight: 1.6 },
  serviceChipRow: { display: "flex", flexWrap: "wrap", gap: "8px" },
  serviceChip:    { display: "inline-flex", alignItems: "center", gap: "6px", background: "#eef2ff", color: "#4338ca", fontSize: "12px", fontWeight: 700, padding: "7px 12px", borderRadius: "999px", border: "1px solid #c7d2fe" },

  addrState: { fontSize: "13px", color: "#6b7280" },
  inlineLink: { color: "#4f46e5", fontWeight: 700, textDecoration: "none" },
  addrList: { display: "flex", flexDirection: "column", gap: "10px" },
  addrItem: { display: "flex", alignItems: "flex-start", gap: "10px", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "12px", cursor: "pointer" },
  addrItemActive: { border: "1.5px solid #4f46e5", background: "#f8faff" },
  addrLabel: { fontSize: "13px", fontWeight: 800, color: "#111827" },
  addrText:  { fontSize: "12px", color: "#6b7280", marginTop: "3px", lineHeight: 1.5 },

  addrTabRow:   { display: "flex", gap: "8px", marginBottom: "14px" },
  addrTab:      { height: "36px", padding: "0 14px", borderRadius: "10px", border: "1px solid #e5e7eb", background: "#fff", color: "#475569", cursor: "pointer", fontWeight: 700, fontSize: "12.5px" },
  addrTabActive:{ border: "1px solid #4f46e5", background: "#eef2ff", color: "#4338ca" },
  testingBadge: { background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", fontSize: "12px", lineHeight: 1.6, borderRadius: "12px", padding: "10px 14px", marginBottom: "12px" },
  detectBtn:    { height: "42px", padding: "0 16px", borderRadius: "12px", border: "1.5px solid #4f46e5", background: "#eef2ff", color: "#4338ca", fontWeight: 800, fontSize: "13px", cursor: "pointer" },
  locationErrorText: { fontSize: "12px", color: "#dc2626", marginTop: "10px" },
  testSelect:   { width: "100%", maxWidth: "360px", height: "42px", padding: "0 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", color: "#111827", fontSize: "13px" },
  mapCard:      { marginTop: "10px", border: "1px solid #e5e7eb", borderRadius: "16px", overflow: "hidden", minHeight: "180px", background: "#f8fafc" },

  summaryCard: { position: "sticky", top: "20px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "20px", padding: "24px 22px" },
  summaryHead: { minHeight: "20px", marginBottom: "6px" },
  popularTag:  { display: "inline-block", background: "#eef2ff", color: "#4338ca", fontSize: "11px", fontWeight: 800, padding: "4px 10px", borderRadius: "999px" },
  summaryPlanName: { margin: 0, fontSize: "20px", fontWeight: 800 },
  summaryTagline:  { margin: "4px 0 16px", fontSize: "12px", color: "#9ca3af", fontWeight: 600 },

  priceRow: { display: "flex", alignItems: "baseline", gap: "6px", marginBottom: "16px" },
  price:    { fontSize: "32px", fontWeight: 800, letterSpacing: "-0.5px" },
  period:   { fontSize: "13px", color: "#9ca3af", fontWeight: 600 },
  gstNote:  { fontSize: "11px", color: "#9ca3af", fontWeight: 600, marginBottom: "12px" },

  summaryDivider: { height: "1px", background: "#eee", margin: "16px 0" },
  summaryRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#6b7280", marginBottom: "10px" },
  summaryVal: { color: "#111827", fontWeight: 700 },
  summaryTotal: { fontSize: "16px", fontWeight: 800, color: "#111827", marginBottom: "18px" },
  summaryServicesWrap: { display: "flex", flexWrap: "wrap", gap: "6px", margin: "4px 0 16px" },
  summaryServiceChip:  { fontSize: "11px", fontWeight: 700, color: "#4338ca", background: "#eef2ff", padding: "5px 10px", borderRadius: "999px" },

  errorBox: { background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "10px 14px", color: "#b91c1c", fontSize: "13px", marginBottom: "14px" },

  primaryBtn: { width: "100%", border: "none", borderRadius: "14px", padding: "15px 0", fontSize: "15px", fontWeight: 800, background: "#4f46e5", color: "#fff", marginBottom: "10px" },
  secureNote: { display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", fontSize: "11px", color: "#9ca3af", fontWeight: 600 },

  testBanner: { background: "#fefce8", border: "1px solid #fde68a", borderRadius: "12px", padding: "12px 16px", fontSize: "14px", color: "#92400e", marginBottom: "18px" },

  successCard:  { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "22px", padding: "40px 34px", textAlign: "center", maxWidth: "420px" },
  successTitle: { margin: "12px 0 8px", fontSize: "24px", fontWeight: 800 },
  successText:  { margin: "0 0 22px", color: "#6b7280", fontSize: "14px", lineHeight: 1.7 },
};

export default MembershipCheckout;
